/**
 * Ce que les écrans d'un cahier ont en commun, côté parent comme côté élève.
 *
 * Déplacé depuis `v2/ChapitresScreen` et `v2/ChapitreScreen` le 5 octobre 2026 :
 * l'élève de lycée feuillette désormais ses propres cahiers pour composer son
 * entraînement (`/eleve/entrainement/cahiers`), et la coquille enfant n'importe
 * ni `parent/` ni `v2/`. Recopiées, la table des niveaux et celle des échelons
 * auraient fini par ne plus dire la même chose au parent et à l'enfant sur le
 * même exercice.
 *
 * Ce qui reste propre à chacun est ce qu'on fait de « pas encore vu en
 * classe » : le parent y répond sur place (`ui/VuEnClasse`, une déclaration),
 * l'élève le lit seulement — c'est un adulte qui atteste qu'un cours a eu lieu.
 * D'où la mention passée par l'appelant.
 */

import { Fragment, type ReactNode } from "react";

import type { LigneDeChapitreOut } from "@/api/schema";
import { Decoupe, pagesDe } from "@/ui/Decoupe";
import { enClair } from "@/ui/dates";

/**
 * Le niveau, tel qu'on l'écrit.
 *
 * Le serveur envoie le jeton du référentiel — « 5e », « terminale ». Un niveau
 * inconnu est rendu **tel quel** plutôt que tu : « 4e » mal composé vaut mieux
 * qu'une phrase qui disparaît le jour où un niveau s'ajoute.
 */
export const NIVEAUX: Record<string, string> = {
  "6e": "6ᵉ",
  "5e": "5ᵉ",
  "4e": "4ᵉ",
  "3e": "3ᵉ",
  seconde: "seconde",
  premiere: "première",
  terminale: "terminale",
};

export function niveauEnClair(niveau: string): string {
  return NIVEAUX[niveau] ?? niveau;
}

/**
 * L'échelon, dit en mots.
 *
 * Le rang vient du serveur, qui le tire de la difficulté Elo de l'exercice ;
 * les quatre marches sont celles de l'ouvrage lui-même. Un nombre nu — « 1 502 »
 * — ne dirait rien à personne, et le mot ne dit rien de plus que ce que le
 * livre imprime déjà en tête de section.
 */
export const ECHELONS: Record<number, string> = {
  1: "pour commencer",
  2: "intermédiaire",
  3: "difficile",
  4: "le plus difficile",
};

/** « Exercice 12 — page 45 », ou ce qui en est connu. */
export function intituleDExercice(exercice: {
  numero?: string | null;
  page?: number | null;
}): string {
  return `${exercice.numero ? `Exercice ${exercice.numero}` : "Exercice"}${
    exercice.page ? ` — page ${exercice.page}` : ""
  }`;
}

/**
 * Une ligne de chapitre, à cocher.
 *
 * Trois mentions l'accompagnent, et **aucune ne barre quoi que ce soit** : déjà
 * fait le tant, pas encore vu en classe, échelon de difficulté.
 *
 * La vignette et les mentions sont **hors du `<label>`** : cliquer l'image
 * agrandit et ne coche pas, et l'une des mentions peut être un bouton — un
 * bouton dans un `<label>` coche la case en même temps qu'il s'ouvre, deux
 * choses pour un geste.
 */
export function ExerciceACocher({
  ligne,
  cochee,
  basculer,
  pasEncoreVu,
}: {
  ligne: LigneDeChapitreOut;
  cochee: boolean;
  basculer: () => void;
  /** Ce qui est dit quand la compétence n'a pas encore été vue en classe. */
  pasEncoreVu: ReactNode;
}) {
  // Des nœuds et non des chaînes : la mention « pas encore vu » est cliquable
  // chez le parent, et un `join(" · ")` sur du texte ne laisserait aucune place
  // pour la rendre telle.
  const mentions: { cle: string; noeud: ReactNode }[] = [];
  if (ligne.fait_le) {
    mentions.push({ cle: "fait", noeud: `fait le ${enClair(ligne.fait_le)}` });
  }
  if (!ligne.vu_en_classe) mentions.push({ cle: "vu", noeud: pasEncoreVu });
  const echelon = ECHELONS[ligne.rang];
  if (echelon) mentions.push({ cle: "echelon", noeud: echelon });
  if (!ligne.corrige) mentions.push({ cle: "corrige", noeud: "sans corrigé de l'éditeur" });
  const intitule = intituleDExercice(ligne);

  return (
    <li className="list__item list__item--pile">
      <label className="check">
        <input type="checkbox" checked={cochee} onChange={basculer} />
        <div>
          <strong>{intitule}</strong>
          <p className="muted small">{ligne.competence}</p>
        </div>
      </label>
      {mentions.length > 0 ? (
        <p className="muted small">
          {mentions.map((mention, rang) => (
            <Fragment key={mention.cle}>
              {rang > 0 ? " · " : null}
              {mention.noeud}
            </Fragment>
          ))}
        </p>
      ) : null}
      {/* Pas de cadre vide quand la réserve n'a pas l'image : un cadre vide se
          lit comme une panne, et l'absence de vignette se lit pour ce qu'elle
          est — une ligne sans découpé. */}
      <Decoupe urls={pagesDe(ligne.enonce_urls, ligne.enonce_url)} intitule={intitule} />
    </li>
  );
}
